// @ts-ignore
// import makeInspectable from 'mobx-devtools-mst'
// import { addMiddleware } from 'mobx-state-tree'
// import { useLocalObservable } from 'mobx-react-lite'
import * as React from 'react'
import { Linking, Platform, ViewProps } from 'react-native'
import { extendLogger, LoggerContexts } from '../utils/logger'
import { StoreContext as MSTGQLStoreContext } from './api-store'
import { useTokenRefreshScheduler } from '../hooks/useTokenRefreshScheduler'
import { useSessionStore } from './session/useSessionStore'
import { handleDeepLink } from './linking'
import { addPushClickListener } from '@/modules/push'
import { PUSH_ANALYTICS_SOURCE } from '@/config/push'

import { getDefaultStore, RootStore } from './root.store'
export const StoreContext = React.createContext<RootStore>({} as RootStore)
export const useStore = () => React.useContext(StoreContext)

const logger = extendLogger(LoggerContexts.StoreProvider)
interface StoreProviderProps extends ViewProps {
  children?: React.ReactNode
}

const StoreProviderInner = ({ children }: StoreProviderProps) => {
  const store = useStore()

  // Add token refresh scheduler (only called when store is ready)
  useTokenRefreshScheduler()

  // Notification taps from any enabled push provider. The facade normalizes each SDK's
  // payload down to the raw link; `source` records which one delivered it.
  React.useEffect(() => {
    if (Platform.OS === 'web') return
    return addPushClickListener((event) => {
      handleDeepLink({
        raw: event.link,
        source: PUSH_ANALYTICS_SOURCE[event.provider],
        track: store.analytics.setEvent
      })
    })
  }, [store.analytics])

  // Customer.io has no JS click callback — its native SDK opens the campaign link
  // itself, so a marketing push arrives here as a Linking url rather than through
  // the listener above. Same resolver, same auth gate; only the `source` differs.
  React.useEffect(() => {
    const subscription = Linking.addEventListener('url', ({ url }) => {
      handleDeepLink({ raw: url, source: 'LinkingWarmStart', track: store.analytics.setEvent })
    })
    return () => subscription.remove()
  }, [store.analytics])

  return <>{children}</>
}

export const StoreProvider = ({ children }: StoreProviderProps) => {
  const [defaultStore, setDefaultStore] = React.useState<RootStore | undefined>(undefined)
  const hasRehydrated = useSessionStore((state) => state.hasRehydrated)
  const incrementAppLaunchCount = useSessionStore((state) => state.incrementAppLaunchCount)

  React.useEffect(() => {
    // Wait for token store to rehydrate before loading MST store
    if (!hasRehydrated) {
      logger.info('Waiting for token store rehydration...')
      return
    }

    const loadStoreFromStorage = async () => {
      logger.info('loadStoreFromStorage - token store rehydrated')
      const store = await getDefaultStore()
      // if (__DEV__) {
      //   // makeInspectable(store)
      //   addMiddleware(store, createMstDebugger(store))
      // }
      logger.info('loadStoreFromStorage', !!store)
      setDefaultStore(store)
    }
    loadStoreFromStorage()
    incrementAppLaunchCount()
  }, [hasRehydrated, incrementAppLaunchCount])

  // const store = useLocalObservable(() => defaultStore)
  // console.log('setup store', !!store)
  if (defaultStore && hasRehydrated) {
    return (
      <StoreContext.Provider value={defaultStore}>
        <MSTGQLStoreContext.Provider value={defaultStore.api}>
          <StoreProviderInner>{children}</StoreProviderInner>
        </MSTGQLStoreContext.Provider>
      </StoreContext.Provider>
    )
  }
  return null
}
